import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';

/** Ảnh sản phẩm theo mã hàng (migration 0054). Không có dữ liệu ảnh trong danh sách, chỉ thông tin. */
export interface ProductImage {
  id: number;
  productId: number;
  mime: string;
  width: number | null;
  height: number | null;
  bytes: number;
  source: 'upload' | 'pexels' | 'unsplash';
  sourceUrl: string | null;
  sourcePage: string | null;
  attribution: string | null;
  license: string | null;
  caption: string | null;
  isPrimary: boolean;
  createdAt: string;
  createdByName: string | null;
}

export const MAX_IMAGES_PER_PRODUCT = 8;
export const IMAGE_SOURCE_LABEL: Record<ProductImage['source'], string> = { upload: 'Tải từ máy', pexels: 'Pexels', unsplash: 'Unsplash' };

const key = (productId: number) => ['product-images', productId] as const;

export const useProductImages = (productId: number | null | undefined) =>
  useQuery({ queryKey: key(productId ?? 0), queryFn: () => api.get<ProductImage[]>(`/products/${productId}/images`), enabled: !!productId });

/** Ảnh cần token đăng nhập → tải blob qua API rồi tạo object URL (thẻ <img> không gửi được header Authorization). */
export function useImageSrc(productId: number, imageId: number, size: 'thumb' | 'full' = 'thumb') {
  const q = useQuery({
    queryKey: ['product-image-file', productId, imageId, size],
    queryFn: () => api.blob(`/products/${productId}/images/${imageId}/file`, { size }),
    staleTime: Infinity,
    gcTime: 10 * 60_000,
  });
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!q.data) return;
    const u = URL.createObjectURL(q.data);
    setSrc(u);
    return () => URL.revokeObjectURL(u);
  }, [q.data]);
  return { src, isError: q.isError };
}

export function useInvalidateImages() {
  const qc = useQueryClient();
  return (productId: number) => qc.invalidateQueries({ queryKey: key(productId) });
}

/** Thu nhỏ ảnh trên trình duyệt trước khi gửi: bản lớn ≤ 1600px, bản nhỏ ≤ 400px, JPG nền trắng. */
async function scaled(bitmap: ImageBitmap, max: number, quality: number): Promise<string> {
  const ratio = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * ratio));
  const h = Math.max(1, Math.round(bitmap.height * ratio));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bitmap, 0, 0, w, h);
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', quality));
  if (!blob) throw new Error('Trình duyệt không xử lý được ảnh');
  const buf = new Uint8Array(await blob.arrayBuffer());
  let bin = '';
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(bin);
}

/** Thu nhỏ 1 file ảnh thành cặp base64 JPG (bản lớn ≤ 1600px, bản nhỏ ≤ 400px). Dùng chung cho ảnh sản phẩm và ảnh đội xe (P12). */
export async function scaleImageFile(file: File): Promise<{ data: string; thumb: string }> {
  if (!/^image\/(jpeg|png|webp|gif|bmp|heic|heif)$/.test(file.type)) throw new Error(`${file.name}: chỉ nhận file ảnh (JPG, PNG…)`);
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error(`${file.name}: không đọc được ảnh`);
  }
  try {
    const [data, thumb] = await Promise.all([scaled(bitmap, 1600, 0.85), scaled(bitmap, 400, 0.8)]);
    return { data, thumb };
  } finally {
    bitmap.close();
  }
}

export async function uploadProductImage(productId: number, file: File, caption?: string) {
  const { data, thumb } = await scaleImageFile(file);
  return api.post<ProductImage>(`/products/${productId}/images`, { data, thumb, caption: caption ?? null });
}

export interface SaveFromSearchInput {
  source: 'pexels' | 'unsplash';
  imageUrl: string;
  thumbUrl: string;
  sourcePage?: string | null;
  attribution?: string | null;
  license?: string | null;
  caption?: string | null;
  downloadTrack?: string | null;
}
export const saveImageFromSearch = (productId: number, input: SaveFromSearchInput) => api.post<ProductImage>(`/products/${productId}/images/from-search`, input);
export const setPrimaryImage = (productId: number, imageId: number) => api.put<ProductImage>(`/products/${productId}/images/${imageId}`, { isPrimary: true });
export const updateImageCaption = (productId: number, imageId: number, caption: string | null) =>
  api.put<ProductImage>(`/products/${productId}/images/${imageId}`, { caption });
export const deleteProductImage = (productId: number, imageId: number) => api.del<{ ok: boolean }>(`/products/${productId}/images/${imageId}`);
